import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useCollaboratorInvitation } from '../../../../../hooks/api/useCollaboratorInvitation';
import urls from '../../../../../utils/urls';
import { getIntl } from '../../../../../utils/intl';
import PermissionsInput from '../../../../shared/PermissionsInput';
import { Field } from 'formik';
import { consentCheck } from '../../../../../utils/validators';
import { useLoadedUser } from '../../../../../hooks/api/useUser';
import getName from '../../../../../utils/getName';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import { useConfirmBackNavigationOnDirtyForm } from '../../../../shared/FormExitValidation';
import { CenteredFormRow, Form } from '../../../../v2BaseComponents/Form';
import Stack, { FormStack } from '../../../../v2BaseComponents/Stack';
import { LoadableAvatar, getFallbackFromName } from '../../../../v2BaseComponents/Avatar';
import Text from '../../../../v2BaseComponents/Text';
import LabelValueDisplay from '../../../../v2BaseComponents/LabelValueDisplay';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';

const intl = getIntl('collaborator-invitation', {
    reject: 'Reject',
    accept: 'Accept',
    consent: `I, {userName}, hereby accept the sharing of {profileName}’s medical information as authorized by {inviterName}.`,
    accessLabel: 'Your Permissions',
    editorLabel: 'Editor',
    invitedBy: 'Invitation by {inviterName}',
    message: 'Message',
});

export type CollaboratorInvitationOwnProps = {};

export type CollaboratorInvitationProps = CollaboratorInvitationOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'invite'>;
};

type FormValue = {
    acceptConsent: boolean;
};

const initialFormValue: FormValue = {
    acceptConsent: false,
};

const CollaboratorInvitation: React.FC<CollaboratorInvitationProps> = ({
    route: {
        params: { profileId, invitationId },
    },
}) => {
    const profilesNavigation = useProfilesTabNavigation();
    useConfirmBackNavigationOnDirtyForm(profilesNavigation);
    const goBack = useGoBack();
    const { user } = useLoadedUser({});
    const { isLoadingInvitation, isRespondingToInvitation, invitation, respondToInvitation } =
        useCollaboratorInvitation({
            medicalProfileId: profileId,
            invitationId,
            onInvitationResponse: () => {
                goBack();
            },
        });

    const userName = getName(user);
    const profileName = invitation ? getName(invitation.medicalProfile) : '';
    const inviterName = invitation
        ? getName({ firstName: invitation.invitedByFirstName, lastName: invitation.invitedByLastName })
        : '';

    return (
        <AnimateLoadedContent isLoading={isRespondingToInvitation || isLoadingInvitation || !invitation}>
            {() =>
                invitation ? (
                    <Form
                        actionContainerSubmit
                        initialValues={initialFormValue}
                        onSubmit={(values, helpers) => {
                            respondToInvitation({ inviteResponse: 'accept' });
                            handleFormikSubmit(values, helpers);
                        }}
                        onCancel={() => respondToInvitation({ inviteResponse: 'reject' })}
                        cancelText={intl('reject')}
                        saveText={intl('accept')}
                        cancelButtonVariant="secondary"
                    >
                        {() => (
                            <FormStack>
                                <Stack vertical space={1} align="center" fullWidth>
                                    <LoadableAvatar
                                        size={'lg'}
                                        avatarUrl={urls.api.collaborators.invitedProfileAvatar(profileId, invitationId)}
                                        fallbackName={getFallbackFromName(profileName)}
                                    />
                                    <Text variant="subtitle-2">{profileName}</Text>
                                    <Text variant="caption-1">
                                        {intl('invitedBy', {
                                            inviterName: getName(invitation.medicalProfile),
                                        })}
                                    </Text>
                                    <Text variant="caption-2">{invitation.invitedByEmail}</Text>
                                </Stack>
                                <CenteredFormRow>
                                    <LabelValueDisplay
                                        items={[
                                            {
                                                label: intl('message'),
                                                value: invitation.message,
                                            },
                                        ]}
                                    />
                                </CenteredFormRow>
                                <CenteredFormRow>
                                    <PermissionsInput
                                        value={invitation.appPermissions!}
                                        isDisabled
                                        onChange={() => {}}
                                        accessLabel={intl('accessLabel')}
                                        editLabel={intl('editorLabel')}
                                        initialAppPermissions={invitation.appPermissions!}
                                    />
                                </CenteredFormRow>
                                <CenteredFormRow>
                                    <Field
                                        name="acceptConsent"
                                        isDisabled={isRespondingToInvitation}
                                        validate={consentCheck}
                                        component={FormikInputs.Checkbox}
                                        title={intl('consent', {
                                            userName,
                                            profileName,
                                            inviterName,
                                        })}
                                        topAlignCheckbox
                                    />
                                </CenteredFormRow>
                            </FormStack>
                        )}
                    </Form>
                ) : null
            }
        </AnimateLoadedContent>
    );
};

export default React.memo(CollaboratorInvitation);
